import { useState } from 'react';
import AppLayout from '@/components/feature/AppLayout';
import { useAuth, User, UserRole } from '@/context/AuthContext';

const roleLabel: Record<UserRole, string> = {
  social_worker: 'Social Worker',
  supervisor: 'Supervisor',
  admin: 'Administrator',
};

const roleBadge: Record<UserRole, string> = {
  social_worker: 'bg-teal-100 text-teal-700',
  supervisor: 'bg-amber-100 text-amber-700',
  admin: 'bg-red-100 text-red-700',
};

const emptyForm = { name: '', email: '', role: 'social_worker' as UserRole, department: 'DSWD Region IV-A', caseload: 0, avatar: '' };

export default function UserManagementPage() {
  const { allUsers, addUser, updateUser, deleteUser } = useAuth();
  const [showModal, setShowModal] = useState(false);
  const [editUser, setEditUser] = useState<User | null>(null);
  const [form, setForm] = useState(emptyForm);
  const [search, setSearch] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const filtered = allUsers.filter((u) =>
    u.name.toLowerCase().includes(search.toLowerCase()) ||
    u.email.toLowerCase().includes(search.toLowerCase())
  );

  const openNew = () => {
    setEditUser(null);
    setForm(emptyForm);
    setShowModal(true);
  };

  const openEdit = (u: User) => {
    setEditUser(u);
    setForm({ name: u.name, email: u.email, role: u.role, department: u.department, caseload: u.caseload, avatar: u.avatar });
    setShowModal(true);
  };

  const handleSave = () => {
    const avatarStr = form.avatar || form.name.split(' ').map((w) => w[0]).join('').toUpperCase().slice(0, 2);
    if (editUser) {
      updateUser(editUser.id, { ...form, avatar: avatarStr });
    } else {
      addUser({ ...form, avatar: avatarStr });
    }
    setShowModal(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
  };

  const handleDelete = (id: string) => {
    deleteUser(id);
    setDeleteConfirm(null);
  };

  return (
    <AppLayout title="User Management" breadcrumb={['Admin', 'User Management']}>
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div className="flex items-center gap-3 bg-white border border-gray-200 rounded-xl px-4 py-2.5 flex-1 min-w-48">
          <i className="ri-search-line text-gray-400 text-sm" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search users..." className="bg-transparent text-sm outline-none w-full text-gray-700 placeholder-gray-400" />
        </div>
        <button onClick={openNew} className="bg-teal-500 text-white text-sm px-5 py-2.5 rounded-xl hover:bg-teal-600 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-2">
          <i className="ri-user-add-line" />Add New User
        </button>
        {saved && (
          <span className="text-xs text-teal-600 flex items-center gap-1 bg-teal-50 border border-teal-200 px-3 py-1.5 rounded-lg">
            <i className="ri-checkbox-circle-line" />User saved successfully!
          </span>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        {(['admin', 'supervisor', 'social_worker'] as UserRole[]).map((r) => (
          <div key={r} className="bg-white rounded-xl border border-gray-100 p-4 text-center">
            <p className="text-2xl font-bold text-gray-900">{allUsers.filter((u) => u.role === r).length}</p>
            <p className="text-sm text-gray-500 mt-1">{roleLabel[r]}s</p>
          </div>
        ))}
      </div>

      {/* User Table */}
      <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-100">
          <h3 className="font-semibold text-gray-800">System Users ({filtered.length})</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 text-xs text-gray-500 uppercase tracking-wide">
                <th className="px-5 py-3 text-left font-medium">User</th>
                <th className="px-5 py-3 text-left font-medium">Email</th>
                <th className="px-5 py-3 text-left font-medium">Role</th>
                <th className="px-5 py-3 text-left font-medium hidden md:table-cell">Department</th>
                <th className="px-5 py-3 text-left font-medium hidden lg:table-cell">Caseload</th>
                <th className="px-5 py-3 text-left font-medium">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {filtered.map((u) => (
                <tr key={u.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-full bg-teal-500 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                        {u.avatar}
                      </div>
                      <p className="text-sm font-medium text-gray-800">{u.name}</p>
                    </div>
                  </td>
                  <td className="px-5 py-3 text-sm text-gray-600">{u.email}</td>
                  <td className="px-5 py-3">
                    <span className={`text-xs px-2 py-1 rounded-full font-medium ${roleBadge[u.role]}`}>{roleLabel[u.role]}</span>
                  </td>
                  <td className="px-5 py-3 hidden md:table-cell text-sm text-gray-500">{u.department}</td>
                  <td className="px-5 py-3 hidden lg:table-cell text-sm text-gray-600">{u.caseload} cases</td>
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-2">
                      <button onClick={() => openEdit(u)} className="text-xs bg-gray-100 text-gray-700 px-3 py-1.5 rounded-lg hover:bg-gray-200 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1">
                        <i className="ri-edit-line" />Edit
                      </button>
                      <button onClick={() => setDeleteConfirm(u.id)} className="text-xs bg-red-50 text-red-600 px-3 py-1.5 rounded-lg hover:bg-red-100 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1">
                        <i className="ri-delete-bin-line" />Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-lg font-bold text-gray-900">{editUser ? 'Edit User' : 'Add New User'}</h3>
              <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600 cursor-pointer"><i className="ri-close-line text-xl" /></button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1.5 block">Full Name <span className="text-red-500">*</span></label>
                <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Full name" className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-teal-500 transition-colors" />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1.5 block">Email Address <span className="text-red-500">*</span></label>
                <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="email@dswd.gov.ph" className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-teal-500 transition-colors" />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1.5 block">Role <span className="text-red-500">*</span></label>
                <div className="grid grid-cols-3 gap-2">
                  {(['social_worker', 'supervisor', 'admin'] as UserRole[]).map((r) => (
                    <button
                      key={r}
                      type="button"
                      onClick={() => setForm({ ...form, role: r })}
                      className={`py-2.5 px-2 rounded-xl border-2 text-xs font-medium transition-all cursor-pointer
                        ${form.role === r ? 'border-teal-500 bg-teal-50 text-teal-700' : 'border-gray-200 text-gray-500 hover:border-gray-300'}`}
                    >
                      {roleLabel[r]}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1.5 block">Department</label>
                <input value={form.department} onChange={(e) => setForm({ ...form, department: e.target.value })} placeholder="e.g. DSWD Region IV-A" className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-teal-500 transition-colors" />
              </div>
            </div>
            <div className="flex gap-3 justify-end mt-6">
              <button onClick={() => setShowModal(false)} className="text-sm border border-gray-200 px-4 py-2 rounded-xl cursor-pointer whitespace-nowrap hover:bg-gray-50 transition-colors">Cancel</button>
              <button onClick={handleSave} disabled={!form.name || !form.email} className="text-sm bg-teal-500 text-white px-6 py-2 rounded-xl hover:bg-teal-600 disabled:bg-teal-300 transition-colors cursor-pointer whitespace-nowrap">
                {editUser ? 'Save Changes' : 'Create User'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirm Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 w-full max-w-sm text-center">
            <div className="w-14 h-14 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-delete-bin-line text-red-500 text-2xl" />
            </div>
            <h3 className="text-lg font-bold text-gray-900 mb-2">Delete User?</h3>
            <p className="text-sm text-gray-500 mb-5">This action cannot be undone. The user will lose access immediately.</p>
            <div className="flex gap-3 justify-center">
              <button onClick={() => setDeleteConfirm(null)} className="text-sm border border-gray-200 px-4 py-2 rounded-xl cursor-pointer whitespace-nowrap hover:bg-gray-50 transition-colors">Cancel</button>
              <button onClick={() => handleDelete(deleteConfirm)} className="text-sm bg-red-500 text-white px-4 py-2 rounded-xl hover:bg-red-600 transition-colors cursor-pointer whitespace-nowrap">Delete User</button>
            </div>
          </div>
        </div>
      )}
    </AppLayout>
  );
}
